/**
 * The one place that knows what a content or topic identifier is.
 *
 * Every identifier that leaves the process — into a URL, localStorage, sessionStorage, IndexedDB or
 * the HTTP wire — goes out through an encode and comes back through a decode. Today both are the
 * identity function: the values are still PubHub's `persistentId` and `resourceKey`. The point is
 * not obfuscation, it is that TB-11956 can change what the value *is* by editing this file plus one
 * data migration, instead of the ~320 references that exist across the two frontends and the backend.
 *
 * Changing these to a real encoding is gated on product decision D3 (migrate saved bookmarks and
 * progress, or accept their loss) — bookmarks live inside vehicles, so this is the one part of the
 * epic a user can see break.
 *
 * Syntax: this module is consumed by diva-mm, and apps/diva-mm/next.config.mjs transpiles only
 * @tweddle-se-team/common-frontend — not this package. Chrome 73 covers ES2018, so const and arrow
 * functions are fine, but optional chaining (`?.`) and nullish coalescing (`??`) are not.
 */

const PUBLICATION_POSTFIX = /^([^-]+-\d+).*/
const TOPIC_VERSION_POSTFIX = /_v1$/

const isMissing = value => value === null || value === undefined

/** Value -> ref, at every boundary out. Identity today; see the module comment. */
export const encodeContentRef = persistentId => persistentId

/** Ref -> value, at every boundary in. */
export const decodeContentRef = ref => ref

/** Value -> ref for a topic. */
export const encodeTopicRef = resourceKey => resourceKey

/** Ref -> value for a topic. */
export const decodeTopicRef = ref => ref

/**
 * Value -> ref for a publication.
 *
 * A third kind of identifier, and deliberately its own pair: diva-mm carries a publication id in the
 * URL to disambiguate a topic between publications, and that id is neither a persistentId nor a
 * resourceKey. Encoding it with a function named for one of those would be a lie the day these stop
 * being identity functions.
 */
export const encodePublicationRef = publicationId => publicationId

/** Ref -> value for a publication. */
export const decodePublicationRef = ref => ref

/**
 * The publication a ref belongs to.
 *
 * Ported verbatim from removeResourceKeyPostfixPublication, which was duplicated in both frontends.
 * removeResourceKeyPostfixVideo used the identical regex under a second name; there is one function
 * here and a test pinning the equivalence.
 *
 * The originals were written as `resourceKey?.replace(...)`; the explicit isMissing check keeps that
 * tolerance without syntax Chrome 73 cannot parse.
 */
export const publicationRefOf = ref => (isMissing(ref) ? ref : String(ref).replace(PUBLICATION_POSTFIX, '$1'))

/** A topic ref without its version postfix. Ported verbatim from removeResourceKeyPostfixTopic. */
export const topicRefOf = ref => (isMissing(ref) ? ref : String(ref).replace(TOPIC_VERSION_POSTFIX, ''))
